/**
 * Campo de Secret com politica de senha validada em tempo real.
 *
 * O valor real permanece exclusivamente no SecretsContext. O formData recebe
 * apenas asteriscos quando a politica e atendida, evitando que a senha apareca
 * na revisao ou no payload comum de parametros do Scaffolder.
 */

import { useState } from 'react';
import { FormControl, FormHelperText, TextField } from '@material-ui/core';
import {
  createFormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import {
  useTemplateSecrets,
  type FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';

export interface PasswordRequirement {
  id: 'length' | 'lowercase' | 'uppercase' | 'number' | 'symbol' | 'linebreak';
  label: string;
  met: boolean;
}

export function evaluatePassword(
  value: string,
  minLength: number,
): PasswordRequirement[] {
  return [
    {
      id: 'length',
      label: `Ao menos ${minLength} caracteres`,
      met: value.length >= minLength,
    },
    { id: 'lowercase', label: 'Uma letra minuscula', met: /[a-z]/.test(value) },
    { id: 'uppercase', label: 'Uma letra maiuscula', met: /[A-Z]/.test(value) },
    { id: 'number', label: 'Um numero', met: /[0-9]/.test(value) },
    {
      id: 'symbol',
      label: 'Um simbolo',
      met: /[^A-Za-z0-9\r\n]/.test(value),
    },
    {
      id: 'linebreak',
      label: 'Sem quebra de linha',
      met: !/[\r\n]/.test(value),
    },
  ];
}

export function passwordIsValid(value: string, minLength: number): boolean {
  return (
    value.length > 0 &&
    evaluatePassword(value, minLength).every(rule => rule.met)
  );
}

export function passwordFormValue(
  value: string,
  minLength: number,
): string | undefined {
  return passwordIsValid(value, minLength)
    ? '*'.repeat(value.length)
    : undefined;
}

export const PasswordPolicySecret = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    name,
    schema,
    uiSchema,
    required,
    disabled,
    readonly,
    onChange,
    onBlur,
    onFocus,
    idSchema,
  } = props;
  const { secrets, setSecrets } = useTemplateSecrets();
  const minLength = Number(schema.minLength ?? 12);
  const [value, setValue] = useState(secrets[name] ?? '');
  const [touched, setTouched] = useState(false);
  const requirements = evaluatePassword(value, minLength);
  const valid = passwordIsValid(value, minLength);
  const description =
    (uiSchema['ui:description'] as string | undefined) ?? schema.description;

  const update = (nextValue: string) => {
    setValue(nextValue);
    setTouched(true);
    setSecrets({ [name]: nextValue });
    const fieldErrors =
      nextValue && !passwordIsValid(nextValue, minLength)
        ? { __errors: ['A senha ainda nao atende a todos os requisitos.'] }
        : {};
    onChange(
      passwordFormValue(nextValue, minLength),
      // ErrorSchema<string> e tipado como string pelo RJSF para campos
      // primitivos, embora o runtime espere o objeto com __errors.
      fieldErrors as any,
      idSchema.$id,
    );
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={(touched && !valid) || Boolean(props.rawErrors?.length)}
      fullWidth
    >
      <TextField
        id={idSchema.$id}
        name={name}
        label={(schema.title as string) ?? 'Senha'}
        value={value}
        type="password"
        autoComplete="new-password"
        variant="outlined"
        required={required}
        disabled={disabled}
        InputProps={{ readOnly: readonly }}
        inputProps={{
          minLength,
          maxLength: schema.maxLength,
          'aria-describedby': `${idSchema.$id}-requirements`,
        }}
        onChange={event => update(event.target.value)}
        onBlur={() => {
          setTouched(true);
          onBlur(idSchema.$id, passwordFormValue(value, minLength));
        }}
        onFocus={() =>
          onFocus(idSchema.$id, passwordFormValue(value, minLength))
        }
      />
      <FormHelperText id={`${idSchema.$id}-requirements`} component="div">
        {description && <div>{description}</div>}
        <ul
          aria-live="polite"
          style={{ margin: '6px 0 0', paddingLeft: 20, columns: 2 }}
        >
          {requirements.map(rule => {
            let color = 'inherit';
            if (value.length > 0) {
              color = rule.met ? '#2e7d32' : '#c62828';
            }

            return (
              <li key={rule.id} style={{ color }}>
                {rule.met ? '✓' : '○'} {rule.label}
              </li>
            );
          })}
        </ul>
      </FormHelperText>
    </FormControl>
  );
};

export const PasswordPolicySecretField = createFormField({
  name: 'PasswordPolicySecret',
  component: PasswordPolicySecret,
});

export const passwordPolicySecretField = FormFieldBlueprint.make({
  name: 'password-policy-secret',
  params: {
    field: async () => PasswordPolicySecretField,
  },
});
